import { useEffect, useMemo, useState } from 'react'
import { Drillable } from '../components/Drill'
import { MarketplaceItem, MarketplaceKind, MarketplaceStatus } from '../lib/domain'
import { useRemoteMarketplace } from '../lib/useRemoteData'
import { patchMarketplace as patchMarketplaceRemote } from '../lib/adminClient'
import { LoadError } from '../components/LoadError'

const STATUS_TONE: Record<MarketplaceStatus, string> = {
  draft:      'bg-slate-500/15 text-slate-300',
  review:     'bg-amber-500/15 text-amber-300',
  published:  'bg-emerald-500/15 text-emerald-300',
  unlisted:   'bg-red-500/15 text-red-300'
}

function formatPrice(cents: number) {
  if (cents === 0) return 'Free'
  return `$${(cents / 100).toFixed(2)}`
}

export default function Marketplace() {
  const [tab, setTab] = useState<MarketplaceKind>('widget')
  const { data: remoteItems, refresh: refreshMarketplace, error: loadError } = useRemoteMarketplace()
  const [saveError, setSaveError] = useState<string | null>(null)
  const [saving, setSaving] = useState(false)

  async function persistEdit(): Promise<void> {
    if (!editing) return
    setSaveError(null)
    setSaving(true)
    const result = await patchMarketplaceRemote(editing.id, {
      title: editing.title,
      blurb: editing.blurb,
      priceCents: editing.priceCents,
      status: editing.status,
      tags: editing.tags
    })
    setSaving(false)
    if (!result.ok) {
      setSaveError(result.error || 'Save failed.')
      return
    }
    await refreshMarketplace()
    setEditing(null)
  }
  const [items, setItems] = useState<MarketplaceItem[]>(remoteItems)
  // Re-seed local state when the remote fetch resolves. Edits stay local
  // (in `items`) until the user clicks Save, mirroring the existing pattern.
  useEffect(() => { setItems(remoteItems) }, [remoteItems])
  const [editing, setEditing] = useState<MarketplaceItem | null>(null)

  const list = useMemo(() => items.filter((i) => i.kind === tab), [items, tab])

  function patch(id: string, p: Partial<MarketplaceItem>) {
    setItems((prev) => prev.map((it) => (it.id === id ? { ...it, ...p } : it)))
    setEditing((e) => (e && e.id === id ? { ...e, ...p } : e))
  }

  const stats = useMemo(() => {
    const widgets = items.filter((i) => i.kind === 'widget')
    const templates = items.filter((i) => i.kind === 'template')
    const installs = items.reduce((s, i) => s + i.installCount, 0)
    const revenueCents = items.reduce((s, i) => s + i.installCount * i.priceCents, 0)
    return { widgets: widgets.length, templates: templates.length, installs, revenueCents }
  }, [items])

  return (
    <div>
      <div className="mb-6 flex items-end justify-between">
        <div>
          <div className="text-[11px] uppercase tracking-[0.3em] text-slate-400">Marketplace</div>
          <h1 className="text-2xl font-semibold mt-1">Widgets & templates</h1>
        </div>
        <button className="btn-primary" disabled title="Not available: the admin API has no create endpoint for marketplace items.">+ New {tab}</button>
      </div>

      {loadError && <LoadError what="marketplace items" endpoint="GET /admin/marketplace" error={loadError} />}

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 mb-6">
        <Drillable to={{ set: 'marketplace', filters: { kind: 'widget' } }} label="Marketplace work widgets" expect={{ key: 'count', value: stats.widgets }} className="glass-card rounded-xl p-4">
          <div className="text-[10px] uppercase tracking-[0.16em] text-slate-400">Widgets</div>
          <div className="text-xl font-semibold mt-1">{stats.widgets}</div>
        </Drillable>
        <Drillable to={{ set: 'marketplace', filters: { kind: 'template' } }} label="Marketplace templates" expect={{ key: 'count', value: stats.templates }} className="glass-card rounded-xl p-4">
          <div className="text-[10px] uppercase tracking-[0.16em] text-slate-400">Templates</div>
          <div className="text-xl font-semibold mt-1">{stats.templates}</div>
        </Drillable>
        <Drillable to={{ set: 'marketplace', filters: {} }} label="Marketplace installs" expect={{ key: 'installs', value: stats.installs }} className="glass-card rounded-xl p-4" testId="marketplace-drill-installs">
          <div className="text-[10px] uppercase tracking-[0.16em] text-slate-400">Installs (lifetime)</div>
          <div className="text-xl font-semibold mt-1">{stats.installs.toLocaleString()}</div>
        </Drillable>
        <Drillable to={{ set: 'marketplace', filters: {} }} label="Installs × list price" expect={{ key: 'listValue', value: stats.revenueCents / 100 }} className="glass-card rounded-xl p-4">
          {/* Not revenue: installs include free installs and the price is today's
              list price, not what was paid. The admin API has no purchase
              ledger, so the tile says exactly what it multiplies. */}
          <div className="text-[10px] uppercase tracking-[0.16em] text-slate-400">Installs × list price</div>
          <div className="text-xl font-semibold mt-1">${(stats.revenueCents / 100).toLocaleString()}</div>
          <div className="text-[11px] text-slate-500 mt-1">Not sales: no purchase ledger yet</div>
        </Drillable>
      </div>

      <div className="flex gap-2 mb-4">
        {(['widget', 'template'] as MarketplaceKind[]).map((k) => (
          <button
            key={k}
            onClick={() => setTab(k)}
            className={`px-4 py-2 rounded-md text-sm border ${tab === k ? 'border-accent bg-accent/10 text-white' : 'border-white/10 text-slate-300 hover:bg-white/5'}`}
          >
            {k === 'widget' ? 'Widgets' : 'Templates'}
          </button>
        ))}
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
        {list.map((it) => (
          <div key={it.id} className="glass-card rounded-xl p-4 flex flex-col">
            <div className="flex items-start justify-between gap-2 mb-2">
              <div className="min-w-0">
                <div className="text-sm font-semibold truncate">{it.title}</div>
                <div className="text-[11px] text-slate-500">by {it.authorHandle}</div>
              </div>
              <span className={`text-[10px] uppercase tracking-wider px-2 py-0.5 rounded-full ${STATUS_TONE[it.status]}`}>{it.status}</span>
            </div>
            <div className="text-xs text-slate-400 leading-relaxed flex-1">{it.blurb}</div>
            <div className="flex flex-wrap gap-1 mt-3">
              {it.tags.map((tag) => (
                <span key={tag} className="text-[10px] uppercase tracking-wider px-2 py-0.5 rounded-full bg-white/5 text-slate-300">{tag}</span>
              ))}
            </div>
            <div className="mt-4 pt-3 border-t border-white/5 flex items-center justify-between text-xs">
              <div className="text-slate-300">
                <span className="font-semibold">{formatPrice(it.priceCents)}</span>
                <span className="text-slate-500 mx-1.5">·</span>
                <span>{it.installCount.toLocaleString()} installs</span>
                {it.rating > 0 && (<><span className="text-slate-500 mx-1.5">·</span><span>★ {it.rating.toFixed(1)}</span></>)}
              </div>
              <button className="btn-ghost text-xs" onClick={() => setEditing(it)}>Edit</button>
            </div>
          </div>
        ))}
        {list.length === 0 && (
          <div className="glass-card rounded-xl p-8 text-center text-slate-500 col-span-full">No {tab}s yet.</div>
        )}
      </div>

      {editing && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 p-6" onClick={() => setEditing(null)}>
          <div className="glass-card rounded-2xl p-6 w-full max-w-lg" onClick={(e) => e.stopPropagation()}>
            <div className="text-[11px] uppercase tracking-[0.3em] text-slate-400">Edit {editing.kind}</div>
            <h2 className="text-lg font-semibold mt-1 mb-4">{editing.title}</h2>
            <div className="grid grid-cols-2 gap-3 text-sm">
              <label className="block col-span-2">
                <div className="text-[10px] uppercase tracking-wider text-slate-400 mb-1">Title</div>
                <input value={editing.title} onChange={(e) => patch(editing.id, { title: e.target.value })}
                  className="w-full bg-black/30 border border-white/10 rounded-md px-2 py-1.5 focus:outline-none focus:border-accent" />
              </label>
              <label className="block col-span-2">
                <div className="text-[10px] uppercase tracking-wider text-slate-400 mb-1">Blurb</div>
                <textarea rows={3} value={editing.blurb} onChange={(e) => patch(editing.id, { blurb: e.target.value })}
                  className="w-full bg-black/30 border border-white/10 rounded-md px-2 py-1.5 focus:outline-none focus:border-accent resize-none" />
              </label>
              <label className="block">
                <div className="text-[10px] uppercase tracking-wider text-slate-400 mb-1">Price (cents)</div>
                <input type="number" value={editing.priceCents} onChange={(e) => patch(editing.id, { priceCents: Number(e.target.value) })}
                  className="w-full bg-black/30 border border-white/10 rounded-md px-2 py-1.5 focus:outline-none focus:border-accent" />
              </label>
              <label className="block">
                <div className="text-[10px] uppercase tracking-wider text-slate-400 mb-1">Status</div>
                <select value={editing.status} onChange={(e) => patch(editing.id, { status: e.target.value as MarketplaceStatus })}
                  className="w-full bg-black/30 border border-white/10 rounded-md px-2 py-1.5 focus:outline-none focus:border-accent">
                  <option value="draft">Draft</option>
                  <option value="review">In review</option>
                  <option value="published">Published</option>
                  <option value="unlisted">Unlisted</option>
                </select>
              </label>
              <label className="block col-span-2">
                <div className="text-[10px] uppercase tracking-wider text-slate-400 mb-1">Tags (comma separated)</div>
                <input value={editing.tags.join(', ')} onChange={(e) => patch(editing.id, { tags: e.target.value.split(',').map((t) => t.trim()).filter(Boolean) })}
                  className="w-full bg-black/30 border border-white/10 rounded-md px-2 py-1.5 focus:outline-none focus:border-accent" />
              </label>
            </div>
            {saveError && (
              <div className="mt-3 text-xs text-red-400">{saveError}</div>
            )}
            <div className="mt-6 flex justify-end gap-2">
              <button className="btn-ghost" onClick={() => setEditing(null)} disabled={saving}>Close</button>
              <button className="btn-primary" onClick={() => void persistEdit()} disabled={saving}>
                {saving ? 'Saving…' : 'Save'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}
